import { Fragment, forwardRef, useImperativeHandle, useRef } from "react";

import { ObjectTypeFragment, useListObjectTypesQuery } from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import cn from "@/helpers/classNames";
import { IntegrationLogoBox } from "@/integrations";
import { Listbox, ListboxProps, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

type Props = ListboxProps<
  "div",
  ObjectTypeFragment | null,
  ObjectTypeFragment
> & {
  isLoading?: boolean;
  options: NonNullable<
    ReturnType<typeof useListObjectTypesQuery>["data"]
  >["objectTypes"];
  connectionLabel: string;
  integrationId: string;
  integrationName: string;
  buttonRef?: React.Ref<HTMLButtonElement>;
};

export const DestinationObjectSelector = forwardRef<
  HTMLDivElement & {
    focus: () => void;
  },
  Props
>(
  (
    {
      options,
      connectionLabel,
      integrationId,
      integrationName,
      buttonRef: buttonRefArg,
      ...listboxProps
    },
    ref,
  ) => {
    const buttonInnerRef = useRef<HTMLButtonElement>(null);

    // Expose the buttonRef to parent
    useImperativeHandle(buttonRefArg, () => buttonInnerRef.current!);

    return (
      <Listbox {...listboxProps} ref={ref} as={Fragment} by="id">
        <div className="relative">
          <Listbox.Button
            ref={buttonInnerRef}
            className="relative flex h-11 w-full cursor-default items-center overflow-hidden rounded-sm border text-left text-sm focus-within:outline focus-within:outline-blue-600"
          >
            <IntegrationLogoBox
              id={integrationId}
              size="xs"
              className="ml-2 h-7 w-7"
            />
            <span className="block truncate py-2 pl-2 pr-10">
              {listboxProps.value?.objectName}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              {listboxProps.isLoading ? (
                <LoadingSpinner className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              )}
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 max-h-80 w-full max-w-md overflow-auto rounded-sm bg-background py-1 text-sm text-foreground shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900">
              <div className="py-1">
                {options.map((obj) => (
                  <Listbox.Option
                    key={obj.id}
                    value={obj}
                    title={obj.objectName}
                    className={({ active, selected }) =>
                      cn(
                        `relative cursor-default select-none py-2 pl-10 pr-4`,
                        {
                          "bg-blue-500 text-white": active || selected,
                        },
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {obj.objectName}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 top-2.5 pl-3">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    );
  },
);
